import type { ReactNode } from 'react';
import styled from 'styled-components';

import type { Currency } from '#src/__generated__/globalTypes.ts';
import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { iconEye } from '#src/atoms/icons/system/iconEye.ts';
import { iconEyeOff } from '#src/atoms/icons/system/iconEyeOff.ts';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, BodyS } from '#src/atoms/typography/index.tsx';
import { Dots } from '#src/atoms/ui/Dots/index.tsx';
import { useAmountWithConversion } from '#src/hooks/useAmountWithConversion.ts';
import { useCryptoBalance } from '#src/hooks/useCryptoBalance.ts';
import { useToggleHideBalance } from '#src/hooks/useToggleHideBalance.tsx';
import type { MonetaryAmount } from '#src/lib/monetaryAmount.ts';

const Container = styled.div<{ $inline: boolean }>`
  display: flex;
  align-items: center;

  ${props =>
    props.$inline ? 'gap: var(--unit);' : `justify-content: space-between`};
`;

const Amount = styled.div`
  text-align: center;
  text-align: left;
`;

const Actions = styled.div`
  display: flex;
  gap: var(--unit);
`;

const Icon = styled(IconButton)<{ $inline: boolean }>`
  margin-bottom: var(--double-unit);
  ${props => (props.$inline ? `margin-bottom: 0;` : '')}
`;

const Line = styled.div`
  display: flex;
  align-items: baseline;
  gap: var(--half-unit);
`;

const MainXL = styled.span`
  font: var(--t-bold) var(--t-32);
`;

type UserBalanceAmountProps = {
  inline?: boolean;
  hideBalance?: boolean;
  monetaryBalance: MonetaryAmount;
  primaryCurrency?: Currency;
};

const UserBalanceAmount = ({
  inline,
  hideBalance,
  monetaryBalance,
  primaryCurrency,
}: UserBalanceAmountProps) => {
  const { main, exponent } = useAmountWithConversion({
    monetaryAmount: monetaryBalance,
    primaryCurrency,
  });

  if (inline)
    return (
      <Line>
        {!!main && (
          <BodyS color="var(--c-white)">
            {hideBalance ? <Dots size="small" count={7} /> : main}
          </BodyS>
        )}
        {!!exponent && (
          <BodyS color="var(--c-nd-600)">
            {hideBalance ? <Dots size="small" count={7} /> : `≈ ${exponent}`}
          </BodyS>
        )}
      </Line>
    );
  return (
    <Vertical gap={0.5}>
      {!!main && (
        <MainXL>{hideBalance ? <Dots size="medium" count={7} /> : main}</MainXL>
      )}
      {!!exponent && (
        <BodyM color="var(--c-nd-600)">
          {hideBalance ? <Dots size="small" count={7} /> : `≈ ${exponent}`}
        </BodyM>
      )}
    </Vertical>
  );
};

type Props = {
  inline?: boolean;
  right?: ReactNode;
  disableToggle?: boolean;
  primaryCurrency?: Currency;
};

export const UserBalance = ({
  inline,
  right,
  disableToggle = false,
  primaryCurrency,
}: Props) => {
  const { toggleHideBalance, loading } = useToggleHideBalance();
  const { hideBalance, monetaryBalance } = useCryptoBalance();

  return (
    <Container $inline={!!inline}>
      <Amount>
        <UserBalanceAmount
          inline={!!inline}
          primaryCurrency={primaryCurrency}
          hideBalance={disableToggle ? false : hideBalance}
          monetaryBalance={monetaryBalance}
        />
      </Amount>
      <Actions>
        {!disableToggle && (
          <Icon
            color="quaternary"
            size="small"
            disableDebounce
            icon={hideBalance ? iconEye : iconEyeOff}
            onClick={() => toggleHideBalance()}
            $inline={!!inline}
            disabled={loading}
          />
        )}
        {right}
      </Actions>
    </Container>
  );
};
